import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import customerService, { type Customer } from "@/services/customer";
import promotionService, {
  type PromotionPayload,
} from "@/services/promotion";
import { hasPermission } from "@/utils/permission";
import {
  normalizePromotionDiscountType,
  parsePromotionDays,
  type PromotionRecord,
} from "@/utils/promotion";

export interface PromotionFormState {
  code: string;
  name: string;
  customer_id: number | null;
  discount_type: "fixed" | "percentage";
  value: number;
  valid_from: string;
  valid_till: string;
  valid_days: string[];
  status: number;
  description: string;
}

const createDefaultForm = (): PromotionFormState => ({
  code: "",
  name: "",
  customer_id: null,
  discount_type: "percentage",
  value: 0,
  valid_from: "",
  valid_till: "",
  valid_days: [],
  status: 1,
  description: "",
});

type PromotionSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function usePromotion() {
  const { t } = useI18n();
  const promotions = ref<PromotionRecord[]>([]);
  const customers = ref<Customer[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedPromotion = ref<PromotionRecord | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const form = ref<PromotionFormState>(createDefaultForm());
  const canCreatePromotion = hasPermission("promotion_create");
  const canUpdatePromotion = hasPermission("promotion_update");
  const canDeletePromotion = hasPermission("promotion_delete");
  const canSaveCurrentPromotion = computed(() =>
    selectedPromotion.value ? canUpdatePromotion : canCreatePromotion
  );

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);
  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("promotion_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("promotion_page.items_per_page") },
  }));

  const formatLocalizedPromotionValue = (
    promotion: Pick<PromotionRecord, "discount_type" | "value">
  ) =>
    normalizePromotionDiscountType(promotion.discount_type) === "percentage"
      ? t("promotion_page.percent_off", {
          value: Number(promotion.value || 0),
        })
      : t("promotion_page.fixed_off", {
          amount: `$${Number(promotion.value || 0).toFixed(2)}`,
        });

  const formatLocalizedPromotionDays = (value?: string | null) => {
    const days = parsePromotionDays(value);
    if (!days.length) return t("promotion_page.every_day");

    return days
      .map((day) => t(`promotion_page.weekday_${day}`))
      .join(", ");
  };

  const formatLocalizedPromotionWindow = (
    promotion: Pick<PromotionRecord, "valid_from" | "valid_till">
  ) => {
    if (promotion.valid_from && promotion.valid_till) {
      return t("promotion_page.date_range", {
        start: promotion.valid_from,
        end: promotion.valid_till,
      });
    }

    if (promotion.valid_from) {
      return t("promotion_page.starts_on", { date: promotion.valid_from });
    }

    if (promotion.valid_till) {
      return t("promotion_page.until_date", { date: promotion.valid_till });
    }

    return t("promotion_page.no_date_limit");
  };

  const fetchInitialData = async () => {
    loading.value = true;
    try {
      const [promotionData, customerData] = await Promise.all([
        promotionService.getPromotions(),
        customerService.getCustomers(),
      ]);
      promotions.value = promotionData;
      customers.value = customerData;
    } catch (error) {
      console.error("Failed to fetch promotions:", error);
      await Swal.fire({
        icon: "error",
        title: t("promotion_page.load_failed"),
        text: t("promotion_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredPromotions = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return promotions.value;

    return promotions.value.filter((promotion) =>
      [
        promotion.code,
        promotion.name,
        promotion.description,
        promotion.customer_name,
        formatLocalizedPromotionDays(promotion.valid_day),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  });

  const stats = computed(() => {
    const total = promotions.value.length;
    const active = promotions.value.filter(
      (promotion) =>
        String(promotion.status ?? 1) !== "0" &&
        String(promotion.plan_status ?? 1) !== "0"
    ).length;
    const customerSpecific = promotions.value.filter(
      (promotion) => Number(promotion.customer_id || 0) > 0
    ).length;

    return {
      total,
      active,
      customerSpecific,
      global: Math.max(total - customerSpecific, 0),
    };
  });

  watch(
    filteredPromotions,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = [
        "10",
        "25",
        "50",
        "100",
        String(totalCount),
      ];
    },
    { immediate: true }
  );

  const resetForm = () => {
    form.value = createDefaultForm();
    errors.value = {};
  };

  const openCreateModal = () => {
    if (!canCreatePromotion) {
      Swal.fire({
        icon: "warning",
        title: t("promotion_page.permission_required"),
        text: t("promotion_page.create_permission_text"),
      });
      return;
    }

    selectedPromotion.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (promotion: PromotionRecord) => {
    if (!canUpdatePromotion) {
      Swal.fire({
        icon: "warning",
        title: t("promotion_page.permission_required"),
        text: t("promotion_page.edit_permission_text"),
      });
      return;
    }

    selectedPromotion.value = promotion;
    errors.value = {};
    form.value = {
      code: promotion.code || "",
      name: promotion.name || "",
      customer_id: promotion.customer_id ? Number(promotion.customer_id) : null,
      discount_type: promotion.discount_type || "percentage",
      value: Number(promotion.value || 0),
      valid_from: promotion.valid_from || "",
      valid_till: promotion.valid_till || "",
      valid_days: promotion.valid_day
        ? promotion.valid_day.split(",").map((day) => day.trim()).filter(Boolean)
        : [],
      status: Number(promotion.status ?? 1),
      description: promotion.description || "",
    };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    errors.value = {};
  };

  const updateField = <K extends keyof PromotionFormState>(
    field: K,
    value: PromotionFormState[K]
  ) => {
    form.value[field] = value;
  };

  const generatePromotionCode = async () => {
    try {
      if (!promotions.value.length) {
        promotions.value = await promotionService.getPromotions();
      }

      const nextNumber =
        promotions.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `PROMO${String(nextNumber).padStart(3, "0")}`;
    } catch (error) {
      console.error("Failed to generate promotion code:", error);
      await Swal.fire({
        icon: "error",
        title: t("promotion_page.generate_failed"),
        text: t("promotion_page.generate_failed_text"),
      });
    }
  };

  const savePromotion = async () => {
    if (!canSaveCurrentPromotion.value) {
      await Swal.fire({
        icon: "warning",
        title: t("promotion_page.permission_required"),
        text: selectedPromotion.value
          ? t("promotion_page.update_permission_text")
          : t("promotion_page.create_permission_text"),
      });
      return;
    }

    loading.value = true;
    errors.value = {};

    try {
      const selectedCustomer =
        customers.value.find(
          (customer) =>
            Number(customer.id || 0) === Number(form.value.customer_id || 0)
        ) || null;

      const payload: PromotionPayload = {
        code: form.value.code.trim(),
        name: form.value.name.trim(),
        customer_id: selectedCustomer?.id ? Number(selectedCustomer.id) : null,
        customer_code: selectedCustomer?.code || null,
        customer_name: selectedCustomer?.name || null,
        value: Number(form.value.value || 0),
        discount_type: form.value.discount_type,
        valid_from: form.value.valid_from || null,
        valid_till: form.value.valid_till || null,
        valid_days: form.value.valid_days,
        status: Number(form.value.status ?? 1),
        description: form.value.description.trim() || null,
      };

      if (selectedPromotion.value) {
        await promotionService.updatePromotion(selectedPromotion.value, payload);
      } else {
        await promotionService.createPromotion(payload);
      }

      promotions.value = await promotionService.getPromotions();

      await Swal.fire({
        icon: "success",
        title: selectedPromotion.value
          ? t("promotion_page.updated_title")
          : t("promotion_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });

      closeModal();
    } catch (error: unknown) {
      console.error("Failed to save promotion:", error);
      const requestError = error as PromotionSaveError;
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        await Swal.fire({
          icon: "error",
          title: t("promotion_page.save_failed"),
          text:
            requestError.response?.data?.message ||
            t("promotion_page.save_failed_text"),
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deletePromotion = async (promotion: PromotionRecord) => {
    if (!canDeletePromotion) {
      await Swal.fire({
        icon: "warning",
        title: t("promotion_page.permission_required"),
        text: t("promotion_page.delete_permission_text"),
      });
      return;
    }

    const result = await Swal.fire({
      title: t("promotion_page.delete_title"),
      text: t("promotion_page.delete_text", { name: promotion.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("promotion_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("promotion_page.deleting_title"),
        text: t("promotion_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await promotionService.deletePromotion(promotion);
      promotions.value = await promotionService.getPromotions();

      await Swal.fire({
        icon: "success",
        title: t("promotion_page.deleted_title"),
        text: t("promotion_page.deleted_text", { name: promotion.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to delete promotion:", error);
      await Swal.fire({
        icon: "error",
        title: t("promotion_page.delete_failed"),
        text: t("promotion_page.delete_failed_text"),
      });
    }
  };

  return {
    customers,
    searchQuery,
    filteredPromotions,
    stats,
    showModal,
    selectedPromotion,
    form,
    errors,
    loading,
    paginationConfig,
    fetchInitialData,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    generatePromotionCode,
    savePromotion,
    deletePromotion,
    canCreatePromotion,
    canUpdatePromotion,
    canDeletePromotion,
    canSaveCurrentPromotion,
    formatPromotionValue: formatLocalizedPromotionValue,
    formatPromotionDays: formatLocalizedPromotionDays,
    formatPromotionWindow: formatLocalizedPromotionWindow,
  };
}
